import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { fetchProject } from "../../../api/project";
import { IMMERSIVE_VIEWER_SHELL_CLASS } from "../../../components/dashboard/immersive-viewer-layout";
import { ifcViewerBackPath } from "../../../components/dashboard/ifc-viewer/ifc-viewer-back";
import {
  IfcViewerPanel,
  readIfcFocusFromSearch,
} from "../../../components/dashboard/ifc-viewer/ifc-viewer-panel";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import {
  useCompanyHasModule,
  useProjectPermissions,
  useUserHasAccess,
} from "../../../routes/roleOnly";

/**
 * Standalone IFC viewer for one project, opened in a new tab from the drawings
 * viewer («Se 3D» / «Åpne sted i 3D»). Focus and house filter come from the URL.
 * Same module/role/executor gates as the project IFC Viewer tab.
 */
export const ProjectIfcViewer = () => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  const moduleActive = useCompanyHasModule("ifc_viewer");
  const canView = useUserHasAccess({
    roles: ["ifc_viewer", "ifc_viewer_edit", "admin"],
  });
  const canEdit = useUserHasAccess({ roles: ["ifc_viewer_edit", "admin"] });
  const isAdmin = useUserHasAccess({ roles: ["admin"] });
  const projectPermissions = useProjectPermissions(project);

  const search = searchParams.toString();
  const focus = useMemo(() => readIfcFocusFromSearch(search), [search]);
  const houseId = searchParams.get("house") || null;
  const drawingId = searchParams.get("drawing");
  const linkDrawingId = searchParams.get("linkDrawing");
  const backPath = ifcViewerBackPath({
    projectId: projectId,
    drawingId,
    linkDrawingId,
  });
  const fromDrawing = backPath.startsWith("/dashboard/drawings/");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetchProject(projectId);
        if (!cancelled) setProject(response?.data || null);
      } catch (_) {
        if (!cancelled) setProject(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  if (!moduleActive || !canView) {
    return <div className="p-6 text-sm text-[#59616d]">{t("ifc_viewer.no_access")}</div>;
  }

  if (loading) {
    return <ResourceLoading label={t("ifc_viewer.loading")} />;
  }

  if (!project?.id) {
    return <div className="p-6 text-sm text-[#59616d]">{t("ifc_viewer.error_load")}</div>;
  }

  // Match project page tab: executors do not get the IFC Viewer surface.
  if (!projectPermissions.canRead || projectPermissions.isExecutorParticipant) {
    return <div className="p-6 text-sm text-[#59616d]">{t("ifc_viewer.no_access")}</div>;
  }

  return (
    <div className={`flex flex-col bg-white ${IMMERSIVE_VIEWER_SHELL_CLASS}`}>
      <header className="flex min-h-14 flex-shrink-0 items-center gap-3 border-b border-[#e5e8ec] px-3.5 pt-[env(safe-area-inset-top,0px)]">
        <Link
          to={ifcViewerBackPath({
            projectId: project.id,
            drawingId,
            linkDrawingId,
          })}
          title={
            fromDrawing ? t("ifc_viewer.back_to_drawing") : t("Back")
          }
          aria-label={
            fromDrawing ? t("ifc_viewer.back_to_drawing") : t("Back")
          }
          className="grid h-11 w-11 place-items-center rounded-[7px] bg-gradient-to-br from-[#2b6ef0] to-[#1d4fb0] text-white"
        >
          <ArrowLeftIcon className="h-4 w-4" />
        </Link>
        <div className="min-w-0 leading-[1.15]">
          <div className="truncate text-[13.5px] font-semibold text-[#14171c]">
            {t("ifc_viewer.iframe_title")}
          </div>
          <div className="truncate font-mono text-[11px] text-[#59616d]">
            {project.displayTitle}
          </div>
        </div>
      </header>
      <div className="min-h-0 flex-1 p-0 md:p-3">
        <IfcViewerPanel
          project={project}
          canEdit={canEdit}
          isAdmin={isAdmin}
          initialHouseId={houseId}
          initialFocus={focus}
          initialLinkDrawingId={searchParams.get("linkDrawing")}
          readUrlParams
          heightClass="h-full min-h-0"
        />
      </div>
    </div>
  );
};
